import { ALL_ITEM_DEFS, getItemDef } from "../items/db.js";
import { isQuestItem } from "../items/types.js";

/**
 * What a thing is worth before anybody haggles: ONE number per item def id, in
 * whole euros, for ONE unit. Every trader price is this times a quantity times
 * that trader's multiplier, so a balance pass is a diff of this list.
 *
 * A table and not `def.valueEuro`: the roster's figure is the tooltip's, this
 * is the tills'. They must agree, and `validatePrices` proves they do. Scale:
 *
 *   2–20      pocket goods;        30–150    things worth a detour;
 *   ~1–10     ammunition PER ROUND (a 30-round stack of Vollmantel is 180 €);
 *   15–620    attachments — factory magazines at the bottom, the 8× scope
 *             nobody stocks at the top;
 *   140–1400  armour;
 *   155–760   guns as RECEIVERS; the counter quotes that plus the factory
 *             parts folded in (`factoryValueEur`), 180–1500;
 *   150–2600  containers — deliberately above every gun. Storage is what a
 *             player saves for.
 *
 * QUEST ITEMS ARE ABSENT: a deliverable is worth nothing across any counter,
 * so `baseValueEur` answers 0 as for an unknown id. `euro` is 1 € per unit; no
 * trader buys it — the settlement credits cash at face value.
 */
export const BASE_VALUE_EUR: Readonly<Record<string, number>> = {
  // --- Weapons: RECEIVERS, not whole guns — the barrels, stocks and glass the
  // guns used to carry integrally are items now and their value was carved out
  // of these entries. `factoryValueEur` folds the tree back up (carbine
  // 320 + 60 + 40 = 420 €); a stripped rifle sells as a 320 € receiver.
  carbine: 320,
  smg: 280,
  shotgun: 170,
  dmr: 580,
  pistol: 155,
  // Dearer than the carbine, cheaper than the hunting rifle: thirty rounds of a
  // real cartridge.
  east_carbine: 760,
  // The dearest gun in the valley: 385 € of receiver plus six factory parts
  // (`ITEM_SNIPER`) that `factoryValueEur` folds back to 1500 € on the shelf.
  sniper: 385,

  // --- Ammunition, PER ROUND ----------------------------------------------
  ammo_8x57_fmj: 6,
  ammo_8x57_sp: 9,
  ammo_9x19_fmj: 3,
  ammo_9x19_hp: 5,
  ammo_12g_buck: 4,
  ammo_12g_slug: 7,
  // Between the 9×19 and the 8×57, like everything else about the cartridge.
  ammo_762_fmj: 5,
  ammo_762_hp: 7,

  // --- Attachments ---------------------------------------------------------
  red_dot: 120,
  // The glass ladder: 2× a first purchase, 4× a saved-for upgrade, 8× dearer
  // than
  // any other part because no trader sells it — a looted one's counter value.
  scope_2x: 200,
  scope_4x: 340,
  scope_8x: 620,
  muzzle_brake_8x57: 90,
  suppressor_9x19: 260,
  ported_choke_12g: 95,
  foregrip: 70,
  recoil_pad: 60,
  thumbhole_stock: 210,
  // Magazines. Factory boxes are the cheapest parts on purpose: a spare has to
  // be bought without thinking.
  carbine_mag_30: 45,
  carbine_mag_40: 110,
  rifle_mag_5: 25,
  rifle_mag_10: 90,
  smg_mag_25: 35,
  smg_drum_45: 300,
  pistol_mag_15: 20,
  shotgun_tube_5: 15,
  shotgun_tube_8: 80,
  // Barrels, priced by steel and threading work; the match barrel is the
  // dearest non-glass part.
  carbine_barrel_long: 130,
  smg_barrel_short: 65,
  smg_barrel_long: 105,
  shotgun_barrel_coach: 85,
  rifle_barrel_match: 280,
  ec_barrel: 80,
  thread_protector: 15,
  slant_brake: 75,
  // The Eastern carbine's furniture. FACTORY parts are cheap: they come on the
  // rifle and must be replaceable without thought.
  stamped_top_cover: 30,
  railed_top_cover: 190,
  bakelite_grip: 25,
  ergo_grip: 95,
  wood_handguard: 35,
  railed_handguard: 160,
  smg_rail_foreend: 120,
  wood_stock: 40,
  folding_stock: 150,
  flashlight: 55,
  laser: 140,
  ec_mag_30: 50,
  ec_mag_10: 30,
  // The five v1 guns' own furniture: cheap for the same reason, each price the
  // slice carved out of its host (60 + 40 out of the carbine's 420) so no shop
  // ticket or sell price moved.
  carbine_barrel: 60,
  carbine_stock: 40,
  smg_barrel: 45,
  wire_stock: 30,
  poly_foreend: 25,
  shotgun_barrel: 55,
  field_stock: 35,
  rifle_barrel: 90,
  walnut_stock: 50,
  // The dearest factory part, and worth finding loose: 4× glass is 340 € new.
  factory_scope_4x: 180,
  pistol_barrel: 25,
  // The sniper rifle's nine — NOT a carve (see `sniper`), priced on what they
  // are
  // worth; the factory six come to 1115 €. Strip it before selling and you are
  // handing over a 385 € action.
  sniper_barrel: 220,
  sniper_barrel_short: 260,
  suppressor_8x57: 420,
  sniper_chassis: 260,
  sniper_chassis_folding: 340,
  sniper_grip: 40,
  sniper_handguard: 60,
  sniper_rail_handguard: 210,
  // The strongest glass in the game, dearer than every scope but the 8×; not
  // stocked either.
  sniper_scope_10x: 520,

  // --- Armour --------------------------------------------------------------
  vest_loden: 180,
  vest_gendarm: 520,
  vest_platte: 1400,
  helmet_stahl: 140,
  helmet_gendarm: 640,

  // --- Medical -------------------------------------------------------------
  med_verband: 25,
  med_schiene: 40,
  med_erstehilfe: 90,
  med_sanikasten: 260,

  // --- Grenades, priced against the ROUNDS beside them on the ammo shelf: a
  // frag is nine 8×57 soft-points. The smoke is cheapest on purpose — a tool
  // that
  // only buys a crossing must be thrown without arithmetic.
  gren_frag: 45,
  gren_smoke: 20,
  gren_flash: 30,

  // --- Rigs, backpacks and storage boxes -----------------------------------
  rig_patronengurt: 150,
  rig_brustgurt: 420,
  pack_wander: 220,
  pack_jagd: 780,
  pack_tourer: 1100,
  pack_expedition: 2400,
  medbox: 600,
  ammobox: 700,
  junkbox: 2600,

  // --- Barter goods --------------------------------------------------------
  speck: 12,
  bergkaese: 20,
  schnaps: 15,
  zigaretten: 8,
  naegel: 4,
  zuendkerzen: 9,
  kupferdraht: 14,
  batterie: 7,
  taschenuhr: 45,
  jagdmesser: 30,
  honigglas: 10,
  feuerzeug: 3,
  streichhoelzer: 2,
  schraubenzieher: 6,
  benzinkanister: 30,
  silberkreuz: 150,
  gamsbart: 400,
  // Game. Meat priced at what it feeds you; the Krickel sits between the pocket
  // watch and the crucifix.
  meat_hare: 18,
  meat_venison: 45,
  gamskrickel: 180,
  euro: 1,
};

/**
 * Base value of one unit of `defId`, or 0 for anything unknown. Never throws,
 * never negative — ids arrive off the wire and out of old stash blobs. Guarded
 * by `hasOwnProperty`, so `baseValueEur("toString")` is 0.
 */
export function baseValueEur(defId: string): number {
  if (!Object.prototype.hasOwnProperty.call(BASE_VALUE_EUR, defId)) return 0;
  const v = BASE_VALUE_EUR[defId];
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : 0;
}

/**
 * Structural check of the table, in the shape of `validateItemDb`: empty means
 * sound. A unit test and a boot assertion, so an unpriced item fails at
 * start-up.
 */
export function validatePrices(): string[] {
  const problems: string[] = [];

  for (const def of ALL_ITEM_DEFS) {
    const price = baseValueEur(def.id);
    // QUEST ITEMS ARE THE ONE EXEMPTION, inverted: they must have NO entry here
    // and say 0 € on the item — the only counter they cross is the quest
    // trade-in.
    if (isQuestItem(def)) {
      if (price !== 0) problems.push(`quest item "${def.id}" is priced at ${price} €`);
      if (def.valueEuro !== 0) {
        problems.push(`quest item "${def.id}" is worth ${def.valueEuro} € in the item DB`);
      }
      continue;
    }
    if (price <= 0) {
      problems.push(`item "${def.id}" has no base value`);
      continue;
    }
    if (!Number.isInteger(price)) {
      problems.push(`item "${def.id}" has a non-integer base value ${price}`);
    }
    // The tooltip and the till read different fields; they may never disagree.
    if (price !== def.valueEuro) {
      problems.push(
        `item "${def.id}" is worth ${price} € here but ${def.valueEuro} € in the item DB`,
      );
    }
  }

  for (const id of Object.keys(BASE_VALUE_EUR)) {
    if (getItemDef(id) === null) problems.push(`price table names unknown item "${id}"`);
  }

  return problems;
}
